import { __assign, __extends } from "tslib";
import { FALLBACK_COLOR } from '@grafana/data';
import tinycolor from 'tinycolor2';
import uPlot from 'uplot';
import { BarAlignment, DrawStyle, GraphGradientMode, LineInterpolation, PointVisibility, } from '../config';
import { PlotConfigBuilder } from '../types';
import { getHueGradientFn, getOpacityGradientFn, getScaleGradientFn } from './gradientFills';
var UPlotSeriesBuilder = /** @class */ (function (_super) {
    __extends(UPlotSeriesBuilder, _super);
    function UPlotSeriesBuilder() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    UPlotSeriesBuilder.prototype.getConfig = function () {
        var _a;
        var _b = this.props, drawStyle = _b.drawStyle, pathBuilder = _b.pathBuilder, pointsBuilder = _b.pointsBuilder, lineInterpolation = _b.lineInterpolation, lineWidth = _b.lineWidth, lineStyle = _b.lineStyle, barAlignment = _b.barAlignment, showPoints = _b.showPoints, pointColor = _b.pointColor, pointSize = _b.pointSize, scaleKey = _b.scaleKey, pxAlign = _b.pxAlign, spanNulls = _b.spanNulls, _c = _b.show, show = _c === void 0 ? true : _c;
        var lineConfig = {};
        if (pathBuilder != null) {
            lineConfig.paths = pathBuilder;
            lineConfig.stroke = this.getLineColor();
            lineConfig.width = lineWidth;
        }
        else if (drawStyle === DrawStyle.Points) {
            lineConfig.paths = function () { return null; };
        }
        else if (drawStyle != null) {
            lineConfig.stroke = this.getLineColor();
            lineConfig.width = lineWidth;
            if (lineStyle && lineStyle.fill !== 'solid') {
                if (lineStyle.fill === 'dot') {
                    lineConfig.cap = 'round';
                }
                lineConfig.dash = (_a = lineStyle.dash) !== null && _a !== void 0 ? _a : [10, 10];
            }
            lineConfig.paths = function (self, seriesIdx, idx0, idx1) {
                var pathsBuilder = mapDrawStyleToPathBuilder(drawStyle, lineInterpolation, barAlignment);
                return pathsBuilder(self, seriesIdx, idx0, idx1);
            };
        }
        var pointsConfig = {
            points: {
                stroke: pointColor,
                fill: pointColor,
                size: pointSize,
            },
        };
        if (pointsBuilder != null) {
            pointsConfig.points.show = pointsBuilder;
        }
        else {
            // we cannot set points.show property above (even to undefined) as that will clear uPlot's default auto behavior
            if (drawStyle === DrawStyle.Points) {
                pointsConfig.points.show = true;
            }
            else {
                if (showPoints === PointVisibility.Auto) {
                    if (drawStyle === DrawStyle.Bars) {
                        pointsConfig.points.show = false;
                    }
                }
                else if (showPoints === PointVisibility.Never) {
                    pointsConfig.points.show = false;
                }
                else if (showPoints === PointVisibility.Always) {
                    pointsConfig.points.show = true;
                }
            }
        }
        return __assign(__assign({ scale: scaleKey, spanGaps: spanNulls, pxAlign: pxAlign,
            show: show, fill: this.getFill() }, lineConfig), pointsConfig);
    };
    UPlotSeriesBuilder.prototype.getLineColor = function () {
        var _a = this.props, lineColor = _a.lineColor, gradientMode = _a.gradientMode, colorMode = _a.colorMode, thresholds = _a.thresholds;
        if (gradientMode === GraphGradientMode.Scheme) {
            return getScaleGradientFn(1, colorMode, thresholds);
        }
        return lineColor !== null && lineColor !== void 0 ? lineColor : FALLBACK_COLOR;
    };
    UPlotSeriesBuilder.prototype.getFill = function () {
        var _a = this.props, lineColor = _a.lineColor, fillColor = _a.fillColor, gradientMode = _a.gradientMode, fillOpacity = _a.fillOpacity, colorMode = _a.colorMode, thresholds = _a.thresholds, theme = _a.theme;
        if (fillColor) {
            return fillColor;
        }
        var mode = gradientMode !== null && gradientMode !== void 0 ? gradientMode : GraphGradientMode.None;
        var opacityPercent = (fillOpacity !== null && fillOpacity !== void 0 ? fillOpacity : 0) / 100;
        switch (mode) {
            case GraphGradientMode.Opacity:
                return getOpacityGradientFn((fillColor !== null && fillColor !== void 0 ? fillColor : lineColor), opacityPercent);
            case GraphGradientMode.Hue:
                return getHueGradientFn((fillColor !== null && fillColor !== void 0 ? fillColor : lineColor), opacityPercent, theme);
            case GraphGradientMode.Scheme:
                return getScaleGradientFn(opacityPercent, colorMode, thresholds);
            default:
                if (opacityPercent > 0) {
                    return tinycolor(lineColor).setAlpha(opacityPercent).toString();
                }
        }
        return undefined;
    };
    return UPlotSeriesBuilder;
}(PlotConfigBuilder));
export { UPlotSeriesBuilder };
var builders = undefined;
function mapDrawStyleToPathBuilder(style, lineInterpolation, barAlignment) {
    if (!builders) {
        // This should be global static, but Jest initalization was failing so we lazy load to avoid the issue
        var pathBuilders = uPlot.paths;
        var barWidthFactor = 0.6;
        var barMaxWidth = Infinity;
        builders = {
            linear: pathBuilders.linear(),
            smooth: pathBuilders.spline(),
            stepBefore: pathBuilders.stepped({ align: -1 }),
            stepAfter: pathBuilders.stepped({ align: 1 }),
            bars: pathBuilders.bars({ size: [barWidthFactor, barMaxWidth] }),
            barsBefore: pathBuilders.bars({ size: [barWidthFactor, barMaxWidth], align: -1 }),
            barsAfter: pathBuilders.bars({ size: [barWidthFactor, barMaxWidth], align: 1 }),
        };
    }
    if (style === DrawStyle.Bars) {
        if (barAlignment === BarAlignment.After) {
            return builders.barsAfter;
        }
        if (barAlignment === BarAlignment.Before) {
            return builders.barsBefore;
        }
        return builders.bars;
    }
    if (style === DrawStyle.Line) {
        if (lineInterpolation === LineInterpolation.StepBefore) {
            return builders.stepBefore;
        }
        if (lineInterpolation === LineInterpolation.StepAfter) {
            return builders.stepAfter;
        }
        if (lineInterpolation === LineInterpolation.Smooth) {
            return builders.smooth;
        }
    }
    return builders.linear; // the default
}
//# sourceMappingURL=UPlotSeriesBuilder.js.map